import productsData from '../data/products.json';
import noticesData from '../data/notices.json';
import statsData from '../data/stats.json';

/**
 * 비동기 로딩 시뮬레이션
 * @param {number} delay - 지연 시간 (ms)
 * @returns {Promise}
 */
const simulateDelay = (delay = 500) => {
  return new Promise((resolve) => setTimeout(resolve, delay));
};

// Mock Data Storage (메모리 상태)
let products = [...productsData];
let notices = [...noticesData];
const stats = { ...statsData };

/**
 * 모든 금융상품 조회
 * @returns {Promise<Array>}
 */
export const getProducts = async () => {
  await simulateDelay();
  return products;
};

/**
 * 특정 금융상품 조회
 * @param {string} id - 상품 ID
 * @returns {Promise<Object|null>}
 */
export const getProductById = async (id) => {
  await simulateDelay();
  return products.find((product) => product.id === id) || null;
};

/**
 * 금융상품 생성
 * @param {Object} productData - 상품 데이터
 * @returns {Promise<Object>}
 */
export const createProduct = async (productData) => {
  await simulateDelay();
  const newProduct = {
    id: `P${String(products.length + 1).padStart(3, '0')}`,
    ...productData,
    listedDate: new Date().toISOString().split('T')[0],
  };
  products.push(newProduct);
  return newProduct;
};

/**
 * 금융상품 수정
 * @param {string} id - 상품 ID
 * @param {Object} updateData - 수정 데이터
 * @returns {Promise<Object|null>}
 */
export const updateProduct = async (id, updateData) => {
  await simulateDelay();
  const index = products.findIndex((product) => product.id === id);
  if (index === -1) return null;

  products[index] = { ...products[index], ...updateData };
  return products[index];
};

/**
 * 금융상품 삭제
 * @param {string} id - 상품 ID
 * @returns {Promise<boolean>}
 */
export const deleteProduct = async (id) => {
  await simulateDelay();
  const index = products.findIndex((product) => product.id === id);
  if (index === -1) return false;

  products.splice(index, 1);
  return true;
};

/**
 * 모든 공시 정보 조회
 * @returns {Promise<Array>}
 */
export const getNotices = async () => {
  await simulateDelay();
  return notices;
};

/**
 * 특정 공시 정보 조회
 * @param {string} id - 공시 ID
 * @returns {Promise<Object|null>}
 */
export const getNoticeById = async (id) => {
  await simulateDelay();
  const notice = notices.find((n) => n.id === id);
  if (notice) {
    // 조회수 증가
    notice.viewCount += 1;
  }
  return notice || null;
};

/**
 * 공시 정보 생성
 * @param {Object} noticeData - 공시 데이터
 * @returns {Promise<Object>}
 */
export const createNotice = async (noticeData) => {
  await simulateDelay();
  const newNotice = {
    id: `N${String(notices.length + 1).padStart(3, '0')}`,
    ...noticeData,
    createdDate: new Date().toISOString().split('T')[0],
    viewCount: 0,
    attachments: noticeData.attachments || [],
  };
  notices.unshift(newNotice);
  return newNotice;
};

/**
 * 공시 정보 수정
 * @param {string} id - 공시 ID
 * @param {Object} updateData - 수정 데이터
 * @returns {Promise<Object|null>}
 */
export const updateNotice = async (id, updateData) => {
  await simulateDelay();
  const index = notices.findIndex((notice) => notice.id === id);
  if (index === -1) return null;

  notices[index] = { ...notices[index], ...updateData };
  return notices[index];
};

/**
 * 공시 정보 삭제
 * @param {string} id - 공시 ID
 * @returns {Promise<boolean>}
 */
export const deleteNotice = async (id) => {
  await simulateDelay();
  const index = notices.findIndex((notice) => notice.id === id);
  if (index === -1) return false;

  notices.splice(index, 1);
  return true;
};

/**
 * 통계 데이터 조회
 * @returns {Promise<Object>}
 */
export const getStats = async () => {
  await simulateDelay(300);
  return stats;
};

/**
 * 로그인 (하드코딩 인증)
 * @param {string} username - 사용자명
 * @param {string} password - 비밀번호
 * @returns {Promise<Object>}
 */
export const login = async (username, password) => {
  await simulateDelay(800);
  if (username === 'admin' && password === 'admin1234') {
    const token = 'mock-jwt-token-' + Date.now();
    return { success: true, token, user: { username: 'admin', role: 'admin' } };
  }
  throw new Error('아이디 또는 비밀번호가 일치하지 않습니다.');
};

/**
 * 로그아웃
 * @returns {Promise<boolean>}
 */
export const logout = async () => {
  await simulateDelay(200);
  return true;
};

/**
 * 상품 가격 차트 데이터 생성 (최근 30일)
 * @param {string} id - 상품 ID
 * @returns {Promise<Array>}
 */
export const getProductPriceHistory = async (id) => {
  await simulateDelay(300);
  const product = products.find((p) => p.id === id);
  if (!product) return [];

  const data = [];
  const today = new Date();
  const currentPrice = product.currentPrice;
  const changeRate = product.changeRate / 100;

  // 30일 전부터 오늘까지의 가격 데이터 생성
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);

    // 랜덤 변동폭 (-3% ~ +3%)
    const randomChange = (Math.random() - 0.5) * 0.06;
    const daysFactor = (30 - i) / 30;
    const price = Math.round(
      currentPrice * (1 - changeRate * daysFactor + randomChange * currentPrice)
    );

    data.push({
      date: date.toISOString().split('T')[0],
      price: price,
      volume: Math.floor(product.volume * (0.7 + Math.random() * 0.6)),
    });
  }

  return data;
};

/**
 * 관련 상품 조회 (동일 기초자산)
 * @param {string} id - 상품 ID
 * @param {number} limit - 조회 개수
 * @returns {Promise<Array>}
 */
export const getRelatedProducts = async (id, limit = 5) => {
  await simulateDelay(300);
  const product = products.find((p) => p.id === id);
  if (!product) return [];

  // 동일 기초자산 또는 동일 카테고리 상품 필터링
  const related = products
    .filter(
      (p) =>
        p.id !== id &&
        (p.underlyingAsset === product.underlyingAsset || p.assetType === product.assetType)
    )
    .slice(0, limit);

  return related;
};

/**
 * Mock Data 초기화 (테스트용)
 */
export const resetMockData = () => {
  products = [...productsData];
  notices = [...noticesData];
};
